/**
 * Discovery (catalog) API client — T0 Epic A.
 *
 * Talks to the public `/catalog` endpoints (no auth, no HMAC) and maps the
 * API payloads into the buyer-facing view models the Feed/Detail screens
 * render (`VehicleSummary`, `VehicleDetailVM`). Keeping the mapping here means
 * the presentation components stay decoupled from the wire shape.
 */
import { apiPublic } from '../../../adapters/api-client/api';
import { TrustMark } from '../../../commodity/components/caria';
import { TrustState } from '../../../commodity/design-tokens';
import { VehicleDetailVM, VehiclePhoto, VehicleSummary } from '../types';

interface ApiTrustMarks {
  verificado: boolean;
  record: boolean;
  tasado: boolean;
  inspected: boolean;
}

// ---- wire shapes (mirror backend discovery read model) ----

interface ApiSellerSummary {
  type: 'private' | 'dealer';
  verified: boolean;
  region?: string;
}

interface ApiVehicleSummary {
  id: string;
  make: string;
  model: string;
  year: number;
  mileageKm: number;
  region?: string;
  sellerPrice: number;
  suggestedPrice?: number | null;
  heroImageUrl?: string;
  vinVerified: boolean;
  trust: ApiTrustMarks;
  seller: ApiSellerSummary;
  createdAt: string;
}

interface ApiVehicleDetail extends ApiVehicleSummary {
  transmission: string;
  fuel: string;
  color: string;
  pricingConfidence?: number | null;
  pricingRangeLow?: number | null;
  pricingRangeHigh?: number | null;
  photos: { url: string; category: string; seq: number }[];
  disclosure?: {
    accidentHistory: boolean;
    previousOwners: number;
    serviceRecordsAvailable: boolean;
    outstandingLiens: boolean;
    knownDefects: string;
    lastServiceDate?: string;
  };
}

interface ApiFeedResponse {
  items: ApiVehicleSummary[];
  nextCursor?: string;
}

export interface CatalogFeedParams {
  make?: string;
  model?: string;
  minPrice?: number;
  maxPrice?: number;
  minYear?: number;
  maxYear?: number;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'mileage_asc';
  limit?: number;
  cursor?: string;
}

export interface CatalogFeedResult {
  vehicles: VehicleSummary[];
  nextCursor?: string;
}

const title = (v: { make: string; model: string; year: number }): string =>
  `${v.make} ${v.model} ${v.year}`;

const vinState = (vinVerified: boolean): TrustState => (vinVerified ? 'verified' : 'unknown');

/** Map the API trust booleans to the renderable TrustMark[] (carfax key = VIN/record). */
const toTrustMarks = (t: ApiTrustMarks): TrustMark[] => [
  { key: 'verificado', label: 'Verificado', on: t.verificado },
  { key: 'carfax', label: 'Record', on: t.record },
  { key: 'tasado', label: 'Tasado', on: t.tasado },
  { key: 'inspected', label: 'Inspected', on: t.inspected },
];

const toSummary = (v: ApiVehicleSummary): VehicleSummary => ({
  id: v.id,
  title: title(v),
  make: v.make,
  model: v.model,
  year: v.year,
  mileageKm: v.mileageKm,
  region: v.region ?? v.seller.region ?? '—',
  heroImageUrl: v.heroImageUrl,
  sellerPrice: v.sellerPrice,
  suggestedPrice: v.suggestedPrice ?? undefined,
  vinState: vinState(v.vinVerified),
  trustMarks: toTrustMarks(v.trust),
});

const toPhotos = (v: ApiVehicleDetail): VehiclePhoto[] =>
  v.photos.map((p) => ({ url: p.url, alt: `${title(v)} — ${p.category}` }));

const toDisclosureText = (d: ApiVehicleDetail['disclosure']): string | undefined => {
  if (!d) return undefined;
  const parts: string[] = [];
  parts.push(
    d.accidentHistory ? 'Historial de accidentes reportado.' : 'Sin accidentes reportados.',
  );
  parts.push(`${d.previousOwners} dueño(s) anterior(es).`);
  if (d.serviceRecordsAvailable) parts.push('Registros de servicio disponibles.');
  if (d.outstandingLiens) parts.push('Tiene gravámenes pendientes.');
  if (d.knownDefects?.trim()) parts.push(`Defectos conocidos: ${d.knownDefects.trim()}`);
  return parts.join(' ');
};

const toDetail = (v: ApiVehicleDetail): VehicleDetailVM => {
  const photoCount = v.photos.length;
  return {
    id: v.id,
    title: title(v),
    status: 'active', // only ACTIVE listings are exposed by the public PDP
    photos: toPhotos(v),
    sellerPrice: v.sellerPrice,
    suggestedPrice: v.suggestedPrice ?? undefined,
    pricingConfidence: v.pricingConfidence ?? undefined,
    specs: {
      make: v.make,
      model: v.model,
      year: v.year,
      mileageKm: v.mileageKm,
      transmission: v.transmission,
      fuel: v.fuel,
      color: v.color,
      vinState: vinState(v.vinVerified),
    },
    trust: {
      vinState: vinState(v.vinVerified),
      disclosureProvided: !!v.disclosure,
      photoCount,
      photoComplete: photoCount >= 6,
      marks: toTrustMarks(v.trust),
    },
    disclosureText: toDisclosureText(v.disclosure),
  };
};

const buildQuery = (params: CatalogFeedParams): string => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, val]) => {
    if (val !== undefined && val !== null && `${val}` !== '') qs.set(k, String(val));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export async function fetchCatalogFeed(params: CatalogFeedParams = {}): Promise<CatalogFeedResult> {
  const res = await apiPublic<ApiFeedResponse>('GET', `/api/v1/catalog${buildQuery(params)}`);
  return {
    vehicles: (res.items ?? []).map(toSummary),
    nextCursor: res.nextCursor,
  };
}

export async function fetchCatalogDetail(id: string): Promise<VehicleDetailVM> {
  const res = await apiPublic<{ vehicle: ApiVehicleDetail }>('GET', `/api/v1/catalog/${id}`);
  return toDetail(res.vehicle);
}
